import React, { FC } from 'react';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { Task, UserRole, useApprovalDeskPermissions, useConfirmationDialogContext } from '@css-xp/apd-common';
import { useApplicationContext } from '@css-xp/base/common-context';
import ApproveModal from '../modal/approve-modal';
import CancelRequestModal from '../modal/cancel-request-modal';
import ClaimModal from '../modal/claim-modal';
import CommentModal from '../modal/comment-modal';
import DeclineModal from '../modal/decline-modal';
import ReplyModal from '../modal/reply-modal';
import RequestReasonModal from '../modal/request-reason-modal';
import UnclaimModal from '../modal/unclaim-modal';
import { TaskAction, isActionAllowed } from './task-actions';

export interface TaskActionsListProps {
  task: Task;
  userRole: UserRole | undefined | null;
  refetchAll?: () => void;
  refetch?: () => void;
}

export const TaskActionsList: FC<TaskActionsListProps> = ({ task, userRole, refetchAll, refetch }) => {
  const { hasTaskManagerWriteAccess } = useApprovalDeskPermissions();
  const userProfile = useApplicationContext();
  const userId = userProfile?.userId?.split('@')[0];
  const { openModal, closeModal } = useConfirmationDialogContext();
  const actions = [];

  // TODO: need to extract the modal content to a common place to be consumed by both task list and task detail
  if (isActionAllowed(TaskAction.REPLY, task, userRole, userId)) {
    actions.push(
      <DropdownItem
        key={task.taskId + '-reply'}
        data-testid="reply-action-button"
        onClick={() =>
          openModal({
            modalContent: <ReplyModal task={task} onComplete={refetch} closeModal={closeModal} />,
          })
        }
      >
        {TaskAction.REPLY}
      </DropdownItem>
    );
  }

  if (isActionAllowed(TaskAction.APPROVE, task, userRole, userId)) {
    actions.push(
      <DropdownItem
        key={task.taskId + '-approve'}
        data-testid="approve-action-button"
        onClick={() =>
          openModal({
            modalContent: <ApproveModal task={task} onComplete={refetch} closeModal={closeModal} />,
          })
        }
      >
        {TaskAction.APPROVE}
      </DropdownItem>
    );
  }

  if (isActionAllowed(TaskAction.DECLINE, task, userRole, userId)) {
    actions.push(
      <DropdownItem
        key={task.taskId + '-decline'}
        data-testid="decline-action-button"
        onClick={() =>
          openModal({
            modalContent: <DeclineModal task={task} onComplete={refetch} closeModal={closeModal} />,
          })
        }
      >
        {TaskAction.DECLINE}
      </DropdownItem>
    );
  }

  if (isActionAllowed(TaskAction.REQUEST_REASONS, task, userRole, userId)) {
    actions.push(
      <DropdownItem
        key={task.taskId + '-request-reason'}
        data-testid="request-reason-action-button"
        onClick={() =>
          openModal({
            modalContent: <RequestReasonModal task={task} onComplete={refetch} closeModal={closeModal} />,
          })
        }
      >
        {TaskAction.REQUEST_REASONS}
      </DropdownItem>
    );
  }

  // Comment is disabled for now. Will be re-enabled once we have better clarity on its integratio with slack
  /* if (isActionAllowed(TaskAction.COMMENT, task, userRole, userId)) {
    actions.push(
      <DropdownItem
        key={task.taskId + '-comment'}
        onClick={() =>
          openModal({
            modalTitle: ModalTitle.COMMENT,
            modalContent: <CommentModal task={task} closeModal={closeModal} />,
          })
        }
      >
        <div data-testid="comment-action-button">{TaskAction.COMMENT}</div>
      </DropdownItem>
    );
  } */

  if (isActionAllowed(TaskAction.CANCEL_REQUEST, task, userRole, userId)) {
    actions.push(
      <DropdownItem
        key={task.taskId + '-cancel-request'}
        data-testid="cancel-request-action-button"
        onClick={() =>
          openModal({
            modalContent: <CancelRequestModal task={task} onComplete={refetch} closeModal={closeModal} />,
          })
        }
      >
        {TaskAction.CANCEL_REQUEST}
      </DropdownItem>
    );
  }

  if (isActionAllowed(TaskAction.ASSIGN_TASK, task, userRole, userId)) {
    actions.push(
      <DropdownItem
        key={task.taskId + '-claim'}
        data-testid="claim-action-button"
        onClick={() =>
          openModal({
            modalContent: <ClaimModal task={task} onComplete={refetchAll} closeModal={closeModal} />,
          })
        }
      >
        {TaskAction.ASSIGN_TASK}
      </DropdownItem>
    );
  }

  if (isActionAllowed(TaskAction.UNASSIGN_TASK, task, userRole, userId)) {
    actions.push(
      <DropdownItem
        key={task.taskId + '-unclaim'}
        data-testid="unclaim-action-button"
        onClick={() =>
          openModal({
            modalContent: <UnclaimModal task={task} onComplete={refetchAll} closeModal={closeModal} />,
          })
        }
      >
        {TaskAction.UNASSIGN_TASK}
      </DropdownItem>
    );
  }

  if (actions.length && hasTaskManagerWriteAccess) {
    return (
      <DropdownMenu
        triggerButtonProps={{ iconBefore: <MoreIcon label="more" />, appearance: 'subtle' }}
        triggerType="button"
        position="bottom right"
        testId={`dropdown-btn-${task.taskId}`}
      >
        <DropdownItemGroup>{actions}</DropdownItemGroup>
      </DropdownMenu>
    );
  }
  return null;
};
